import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from "react";
import PageWrapper from "../components/PageWrapper";
import { LanguageContext } from "../contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MoneyBagGamePage = ({ onClose, balance, onBalanceUpdate = () => {} }) => {
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [prizeAmount, setPrizeAmount] = useState(0);
  const [serverGamePrize, setServerGamePrize] = useState(0); // Actual prize from server
  const [isScratched, setIsScratched] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [isReadyModalOpen, setIsReadyModalOpen] = useState(true);
  const [isReadyModalFadingOut, setIsReadyModalFadingOut] = useState(false);
  const [isEndGameModalOpen, setIsEndGameModalOpen] = useState(false);
  const [isEndGameModalFadingOut, setIsEndGameModalFadingOut] = useState(false);

  const [revealedValues, setRevealedValues] = useState(Array(6).fill(null));
  const [showPrizesList, setShowPrizesList] = useState(false);

  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef(null);
  const scratchThreshold = 70;
  const hiddenValues = useRef(Array(6).fill(null));
  const scratchCircles = useRef([]);
  const moneyBagImage = useRef(new Image());
  const sparkleImage = useRef(new Image());
  const revealedValuesRef = useRef(revealedValues);

  useEffect(() => {
    revealedValuesRef.current = revealedValues;
  }, [revealedValues]);

  const GAME_COST = 5;
  const NUM_CIRCLES = 6;

  // Define ALL_POSSIBLE_PRIZE_VALUES that can be displayed on the card, regardless of winnability
  // This array defines the numbers that can appear under the scratch-off layer.
  // It is crucial that these values match what the backend uses for generating random non-winning values.
  const ALL_POSSIBLE_PRIZE_VALUES = useMemo(() => [
    0, 5, 10, 15, 25, 100, 500, 1000, 5000, 10000, 25000, 75000, 150000
  ].sort((a,b)=>a-b), []); // Keep sorted for consistency

  // PRIZE_OPTIONS for display in the modal (all positive possible values)
  const PRIZE_OPTIONS = useMemo(() => {
    return ALL_POSSIBLE_PRIZE_VALUES.filter(p => p > 0).sort((a, b) => a - b);
  }, [ALL_POSSIBLE_PRIZE_VALUES]);

  const SYMBOL_SIZE = 80;
  const PADDING = 20;
  const CIRCLE_RADIUS = 60;
  const GRID_COLS = 2;
  const GRID_ROWS = 3;
  const HORIZONTAL_SPACING = 40;
  const VERTICAL_SPACING = 40;

  const translations = useMemo(
    () => ({
      en: {
        title: "Money Bag Scratch Card",
        loadingGame: "Loading game...",
        scratchToReveal: "Scratch to Reveal!",
        youWon: "You Won: {amount} Birr!",
        betterLuckNextTime: "Better Luck Next Time!",
        playAgain: "Play Again",
        goBack: "Go Back to Dashboard",
        birr: "Birr",
        etb: "ETB",
        scratchArea: "Scratch Area",
        prizeArea: "Prize Area",
        insufficientBalance: "Insufficient balance to play. Please top up.",
        networkError: "Network error. Please try again.",
        gameError: "An error occurred during the game. Please try again.",
        processing: "Processing...",
        playButton: "Play for {cost} Birr",
        readyToPlay: "Ready to Play?",
        gameDescription:
          "Each game costs {cost} Birr. Scratch the money bag and match three identical cash amounts to win big! Ready to test your luck?",
        prizes: "Prizes",
        play: "Play",
        cancel: "Cancel",
        scratchTheIcons:
          "Scratch the money bag icons, and if you reveal three identical cash amounts, you win that amount instantly!",
        gameStarted: "Game Started! Scratch the money bags to reveal prizes!",
        congratulations: "Congratulations!",
        error: "Error",
        failedToStartGame: "Failed to start game. Please try again.",
        hidePrizes: "Hide Prizes",
        showPrizes: "Show Prizes",
        tryAgain: "Try again to hit the jackpot!",
      },
      am: {
        title: "የገንዘብ ቦርሳ ከሽከሽ እጣ",
        loadingGame: "ጨዋታ በመጫን ላይ...",
        scratchToReveal: "ለመግለጥ ይከሽክሹ!",
        youWon: "አሸንፈዋል: {amount} ብር!",
        betterLuckNextTime: "በሚቀጥለው ጊዜ መልካም ዕድል!",
        playAgain: "እንደገና ይጫወቱ",
        goBack: "ወደ ዳሽቦርድ ይመለሱ",
        birr: "ብር",
        etb: "ETB",
        scratchArea: "የመከሽከሻ ቦታ",
        prizeArea: "የሽልማት ቦታ",
        insufficientBalance: "መጫወት የማይችል በቂ ሒሳብ የለም። እባክዎ ይሙሉ.",
        networkError: "የኔትወርክ ስህተት። እባክዎ እንደገና ይሞክሩ።",
        gameError: "በጨዋታው ወቅት ስህተት ተከስቷል። እባክዎ እንደገና ይሞክሩ።",
        processing: "በማስኬድ ላይ...",
        playButton: "ለ{cost} ብር ይጫወቱ",
        readyToPlay: "ለመጫወት ዝግጁ ኖት?",
        gameDescription:
          "እያንዳንዱ ጨዋታ {cost} ብር ያስከፍላል። የገንዘብ ቦርሳውን ከሽክሽከው ሶስት ተመሳሳይ የገንዘብ መጠኖችን በማዛመድ ትልቅ ያሸንፉ! ዕድልዎን ለመሞከር ዝግጁ ኖት?",
        prizes: "ሽልማቶች",
        play: "ይጫወቱ",
        cancel: "ይቅር",
        scratchTheIcons:
          "የገንዘብ ቦርሳ አዶዎችን ይከሽክሹ፣ እና ሶስት ተመሳሳይ የገንዘብ መጠኖችን ካገኙ ያንን መጠን ወዲያውኑ ያሸንፋሉ!",
        gameStarted: "ጨዋታው ተጀምሯል! ሽልማቶችን ለማየት የገንዘብ ቦርሳዎችን ይፋቁ!",
        congratulations: "እንኳን ደስ አለዎት!",
        error: "ስህተት",
        failedToStartGame: "ጨዋታውን ማስጀመር አልተሳካም። እባክዎ እንደገና ይሞክሩ።",
        hidePrizes: "ሽልማቶችን ደብቕ",
        showPrizes: "ሽልማቶችን አሳይ",
        tryAgain: "ጃክፖቱን ለመቀላቀል እንደገና ይሞክሩ!",
      },
      or: {
        title: "Malaqaa Boorsaa Kaardii Qarqaraa",
        loadingGame: "Tapha fe'aa jira...",
        scratchToReveal: "Saaxiluuf Qarqari!",
        youWon: "Ati Mo'atte: {amount} Birr!",
        betterLuckNextTime: "Yeroo Itti Aanu Milkaa'ina Gaarii!",
        playAgain: "Deebii Taphadhu",
        goBack: "Gara Daashboordii Deebi'i",
        birr: "Birr",
        etb: "ETB",
        scratchArea: "Iddoo Qarqaraa",
        prizeArea: "Iddoo Badhaasaa",
        insufficientBalance: "Baay'ina gahaa hin qabdu. Mee guuttadhu.",
        networkError: "Dogoggora networkii. Mee deebii yaali.",
        gameError: "Dogoggorri tapha keessatti uumameera. Mee deebii yaali.",
        processing: "Hojjechaa jira...",
        playButton: "Qarshii {cost}f Taphadhu",
        readyToPlay: "Taphaaf Qophooftaa?",
        gameDescription:
          "Tapha tokkoo qarshii {cost} baasii qaba. Boorsaa malaqaa qarqariitii fi qarshii sadii walfakkaataa argadhu! Carraa kee qoruuf qophooftaa?",
        prizes: "Badhaasota",
        play: "Taphadhu",
        cancel: "Haqi",
        scratchTheIcons:
          "Akaakilee boorsaa malaqaa qarqari, yoo qarshii sadii walfakkaataa argatte, battalumaan badhaasa sana mo'atta!",
        gameStarted:
          "Taphi Eegaleera! Badhaasota argachuuf boorsaa malaqaa qarqari!",
        congratulations: "Baga Gammaddan!",
        error: "Dogoggora",
        failedToStartGame: "Tapha eegaluun hin danda'amne. Mee deebii yaali.",
        hidePrizes: "Badhaasota Dhoksi",
        showPrizes: "Badhaasota Agarsiisi",
        tryAgain: "Jaakpoottii argachuuf deebii yaali!",
      },
      ti: {
        title: "ካርታ ገንዘብ ቦርሳ ምፍሓቕ",
        loadingGame: "ጸወታ ይጽዕን ኣሎ...",
        scratchToReveal: "ንምግላጽ ፍሓቕ!",
        youWon: "ኣቲ ተዓዊትኪ: {amount} ቅርሺ!",
        betterLuckNextTime: "ቀጺሉ ዝመጽእ ግዜ ዕድል ይግበረልኪ!",
        playAgain: "እንደገና ተጻወት",
        goBack: "ናብ ዳሽቦርድ ተመለስ",
        birr: "ብር",
        etb: "ETB",
        scratchArea: "ቦታ ምፍሓቕ",
        prizeArea: "ቦታ ሽልማት",
        insufficientBalance: "ዘይምእኩል ሒሳብ ንምጽዋት. እባኽካ ምላእ.",
        networkError: "ጌጋ ኔትወርክ. እባኻ እንደገና ይሞክሩ.",
        gameError: "ሓደ ጌጋ ኣብ እዋን ጸወታ ኣጋጢሙ. እባኻ እንደገና ይሞክሩ.",
        processing: "ይስራሕ ኣሎ...",
        playButton: "ን{cost} ቅርሺ ተጻወት",
        readyToPlay: "ንኽትጻወት ድሉው ድኻ?",
        gameDescription:
          "ነፍሲ ወከፍ ጸወታ {cost} ቅርሺ የኽፍል. ገንዘብ ቦርሳ ፍሓቕ እሞ ሰለስተ ማዕረ መጠን ገንዘብ እንተረኺብካ ዓቢ ትዕወት! ዕድልካ ንምፍታን ድሉው ድኻ?",
        prizes: "ሽልማቶች",
        play: "ተጻወት",
        cancel: "ሰርዝ",
        scratchTheIcons:
          "ኣይኮናት ገንዘብ ቦርሳ ፍሓቕ፣ እሞ ሰለስተ ማዕረ መጠን ገንዘብ እንተረኺብካ፣ ነቲ መጠን ብቕጽበት ትዕወቶ!",
        gameStarted: "ጸወታ ተጀሚሩ! ሽልማቶችን ለማየት የገንዘብ ቦርሳዎችን ይፋቁ!",
        congratulations: "እንኳዕ ደስ በለኩም!",
        error: "ጌጋ",
        failedToStartGame: "ጸወታ ክጅምር ኣይከኣለን. እባኻ እንደገና ይሞክሩ.",
        hidePrizes: "ሽልማቶችን ደብቕ",
        showPrizes: "ሽልማቶችን አሳይ",
        tryAgain: "ጃክፖት ንምርካብ እንደገና ይሞክሩ!",
      },
      wo: {
        title: "Mallaqa Boorsaa Scratch Card",
        loadingGame: "Tapha woyitaa xaa'i...",
        scratchToReveal: "Qarqaqqa'a!",
        youWon: "Ne mo'ay: {amount} Birr!",
        betterLuckNextTime: "Wolqaamoytaa taay!",
        playAgain: "Deemmoo taay",
        goBack: "Dashboardi ba'a",
        birr: "Birr",
        etb: "ETB",
        scratchArea: "Qarqaqqa'a",
        prizeArea: "Badhaasaa",
        insufficientBalance: "Baay'ina gahaa hin qabdu. Mee guuttadhu.",
        networkError: "Network dogoggora. Mee deebii yaali.",
        gameError: "Tapha keessatti dogoggorri uumameera. Mee deebii yaali.",
        processing: "Hojjechaa jira...",
        playButton: "Qarshii {cost}f Taphadhu",
        readyToPlay: "Taphaaf Qophooftaa?",
        gameDescription:
          "Tapha tokkoo qarshii {cost} baasii qaba. Boorsaa malaqaa qarqariitii fi qarshii sadii walfakkaataa argadhu! Carraa kee qoruuf qophooftaa?",
        prizes: "Badhaasota",
        play: "Taphadhu",
        cancel: "Haqi",
        scratchTheIcons:
          "Akaakilee boorsaa malaqaa qarqari, yoo qarshii sadii walfakkaataa argatte, battalumaan badhaasa sana mo'atta!",
        gameStarted:
          "Taphi Eegaleera! Badhaasota argachuuf boorsaa malaqaa qarqari!",
        congratulations: "Baga Gammaddan!",
        error: "Dogoggora",
        failedToStartGame: "Tapha eegaluun hin danda'amne. Mee deebii yaali.",
        hidePrizes: "Badhaasota Dhoksi",
        showPrizes: "Badhaasota Agarsiisi",
        tryAgain: "Jaakpoottii argachuuf deebii yaali!",
      },
    }),
    [language]
  );

  const t = useCallback(
    (key, variables = {}) => {
      let translation =
        translations[language]?.[key] || translations.en[key] || key;
      for (const [placeholder, value] of Object.entries(variables)) {
        translation = translation.replace(`{${placeholder}}`, value);
      }
      return translation;
    },
    [language, translations]
  );

  const drawMoneyBag = useCallback((ctx, x, y, size) => {
    ctx.beginPath();
    ctx.moveTo(x + size * 0.2, y);
    ctx.lineTo(x + size * 0.8, y);
    ctx.quadraticCurveTo(x + size, y + size * 0.2, x + size, y + size * 0.5);
    ctx.lineTo(x + size, y + size * 0.8);
    ctx.quadraticCurveTo(x + size * 0.8, y + size, x + size * 0.5, y + size);
    ctx.quadraticCurveTo(x + size * 0.2, y + size, x, y + size * 0.8);
    ctx.lineTo(x, y + size * 0.5);
    ctx.quadraticCurveTo(x, y + size * 0.2, x + size * 0.2, y);
    ctx.closePath();
    ctx.fillStyle = "#FFD700";
    ctx.fill();
    ctx.strokeStyle = "#DAA520";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(x + size * 0.5, y + size * 0.15, size * 0.15, 0, Math.PI * 2);
    ctx.fillStyle = "#DAA520";
    ctx.fill();

    ctx.font = `${size * 0.4}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#8B4513";
    ctx.fillText("$", x + size * 0.5, y + size * 0.55);
  }, []);

  useEffect(() => {
    moneyBagImage.current.src = "./icons/money_bag.png";
    moneyBagImage.current.onerror = () => {
      moneyBagImage.current = null;
    };

    sparkleImage.current.src = "./icons/sparkle.gif";
    sparkleImage.current.onerror = () => {
      sparkleImage.current = null;
    };
  }, []);

  const initializeCanvas = useCallback(
    (valuesToDraw, drawScratchLayer = true) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      const rect = canvas.getBoundingClientRect();

      canvas.width = rect.width;
      canvas.height = rect.height;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const startX =
        (canvas.width -
          (GRID_COLS * (CIRCLE_RADIUS * 2) +
            (GRID_COLS - 1) * HORIZONTAL_SPACING)) /
        2;
      const startY =
        (canvas.height -
          (GRID_ROWS * (CIRCLE_RADIUS * 2) +
            (GRID_ROWS - 1) * VERTICAL_SPACING)) /
        2;

      scratchCircles.current = [];

      valuesToDraw.forEach((value, index) => {
        const col = index % GRID_COLS;
        const row = Math.floor(index / GRID_COLS);

        const centerX =
          startX +
          col * (CIRCLE_RADIUS * 2 + HORIZONTAL_SPACING) +
          CIRCLE_RADIUS;
        const centerY =
          startY + row * (CIRCLE_RADIUS * 2 + VERTICAL_SPACING) + CIRCLE_RADIUS;

        scratchCircles.current.push({
          x: centerX,
          y: centerY,
          r: CIRCLE_RADIUS,
          fullyScratched: false,
        });

        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, CIRCLE_RADIUS, 0, Math.PI * 2);
        ctx.closePath();

        if (sparkleImage.current && sparkleImage.current.complete) {
          ctx.clip();
          const imgSize = CIRCLE_RADIUS * 2;
          const imgX = centerX - CIRCLE_RADIUS;
          const imgY = centerY - CIRCLE_RADIUS;
          ctx.drawImage(sparkleImage.current, imgX, imgY, imgSize, imgSize);
        } else {
          ctx.fillStyle = "#1a1a1a";
          ctx.fill();
        }

        ctx.strokeStyle = "#4a4a4a";
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.font = `bold ${CIRCLE_RADIUS * 0.4}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#FFFFFF";
        
        // Determine the value to display. If the actual value is 0,
        // pick a random positive value from PRIZE_OPTIONS for display purposes.
        let displayValue = value;
        if (value === 0) {
            const randomIndex = Math.floor(Math.random() * PRIZE_OPTIONS.length);
            displayValue = PRIZE_OPTIONS[randomIndex];
        }
        ctx.fillText(`${displayValue.toLocaleString()}`, centerX, centerY);
        ctx.restore();

        if (drawScratchLayer) {
          if (moneyBagImage.current && moneyBagImage.current.complete) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(centerX, centerY, CIRCLE_RADIUS, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();
            const imgSize = CIRCLE_RADIUS * 2;
            const imgX = centerX - CIRCLE_RADIUS;
            const imgY = centerY - CIRCLE_RADIUS;
            ctx.drawImage(moneyBagImage.current, imgX, imgY, imgSize, imgSize);
            ctx.restore();
          } else {
            ctx.save();
            ctx.beginPath();
            ctx.arc(centerX, centerY, CIRCLE_RADIUS, 0, Math.PI * 2);
            ctx.fillStyle = "#C8C8C8";
            ctx.fill();
            ctx.closePath();
            ctx.restore();
          }

          ctx.save();
          ctx.beginPath();
          ctx.arc(centerX, centerY, CIRCLE_RADIUS, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(153, 153, 153, 0.8)";
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.restore();
        }
      });

      setIsScratched(false);
      setRevealedValues(Array(NUM_CIRCLES).fill(null));
      isDrawing.current = false;
      lastPoint.current = null;
    },
    [
      PADDING,
      CIRCLE_RADIUS,
      NUM_CIRCLES,
      GRID_COLS,
      GRID_ROWS,
      HORIZONTAL_SPACING,
      VERTICAL_SPACING,
      SYMBOL_SIZE,
      sparkleImage.current?.complete,
      PRIZE_OPTIONS // Added PRIZE_OPTIONS as a dependency
    ]
  );

  const getCellIndex = useCallback((pointX, pointY) => {
    for (let i = 0; i < scratchCircles.current.length; i++) {
      const circle = scratchCircles.current[i];
      const distance = Math.sqrt(
        Math.pow(pointX - circle.x, 2) + Math.pow(pointY - circle.y, 2)
      );
      if (distance <= circle.r) {
        return i;
      }
    }
    return -1;
  }, []);

  const calculateAreaScratchPercentage = useCallback((areaIndex) => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;

    const ctx = canvas.getContext("2d");
    const circle = scratchCircles.current[areaIndex];

    const imageData = ctx.getImageData(
      circle.x - circle.r,
      circle.y - circle.r,
      circle.r * 2,
      circle.r * 2
    );
    const pixels = imageData.data;
    let transparentPixels = 0;
    let totalPixelsInCircle = 0;

    for (let y = 0; y < circle.r * 2; y++) {
      for (let x = 0; x < circle.r * 2; x++) {
        const dist = Math.sqrt(
          Math.pow(x - circle.r, 2) + Math.pow(y - circle.r, 2)
        );
        if (dist <= circle.r) {
          totalPixelsInCircle++;
          const pixelIndex = (y * (circle.r * 2) + x) * 4;
          if (pixels[pixelIndex + 3] < 128) {
            transparentPixels++;
          }
        }
      }
    }
    return totalPixelsInCircle > 0
      ? (transparentPixels / totalPixelsInCircle) * 100
      : 0;
  }, []);

  const startGameConfirmed = useCallback(async () => {
    if (balance < GAME_COST) {
      toast.error(t("insufficientBalance"));
      navigate("/wallet");
      return;
    }

    setIsTransitioning(true);
    setIsReadyModalFadingOut(true);

    setTimeout(async () => {
      setIsReadyModalOpen(false);
      setIsEndGameModalOpen(false);

      setLoading(true);
      setGameStarted(false);
      setPrizeAmount(0);
      setServerGamePrize(0); // Reset server prize
      setIsScratched(false);
      setRevealedValues(Array(NUM_CIRCLES).fill(null));

      initializeCanvas(Array(NUM_CIRCLES).fill(0), true);

      try {
        // Prize is now determined by the backend, remove client-side prize selection logic
        const response = await axiosInstance.post("/scratch/new", {
          gameType: "money_bag",
          cost: GAME_COST,
          // prizeAmount removed from payload here, as backend generates it
        });

        if (response.data.success) {
          setCardId(response.data.card.id);
          const actualServerPrize = response.data.card.prize_amount;
          setServerGamePrize(actualServerPrize); // Store the actual prize from the server

          const generatedValues = [];

          if (actualServerPrize > 0) {
            // If the server determines a prize, ensure 3 matching values are displayed
            const winningValue = actualServerPrize;
            for (let i = 0; i < 3; i++) {
              generatedValues.push(winningValue);
            }
            const nonWinningOptions = ALL_POSSIBLE_PRIZE_VALUES.filter(
              (p) => p !== winningValue && p !== 0 // Ensure non-winning options are not the winning value, and prefer non-zero for variety
            );
            // Fill the rest with random non-winning values
            for (let i = 3; i < NUM_CIRCLES; i++) {
              let randomNonWinningValue =
                nonWinningOptions[
                  Math.floor(Math.random() * nonWinningOptions.length)
                ];
              generatedValues.push(randomNonWinningValue);
            }
          } else { // Actual prize is 0, generate a losing card
            const nonZeroDisplayablePrizes = ALL_POSSIBLE_PRIZE_VALUES.filter(p => p > 0);

            // Function to count occurrences of a value in an array
            const countOccurrences = (arr, val) => arr.filter(v => v === val).length;

            // Function to check if adding a value would create a triple with existing values
            const wouldCreateTriple = (currentValues, newValue) => {
              const testValues = [...currentValues, newValue];
              const counts = {};
              testValues.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
              // Check if any value now has 3 or more occurrences AND is a positive prize
              return Object.entries(counts).some(([valStr, count]) => count >= 3 && parseFloat(valStr) > 0);
            };

            let tempGeneratedValues = [];
            for (let i = 0; i < NUM_CIRCLES; i++) {
                let valueToAdd = null;
                let attempts = 0;
                const maxAttempts = 100; // Limit attempts to prevent infinite loops

                do {
                    // Pick a random value from possible non-zero prizes, or 0
                    valueToAdd = nonZeroDisplayablePrizes[Math.floor(Math.random() * nonZeroDisplayablePrizes.length)];
                    // Removed the 20% chance to add a 0 value to ensure no 0s are picked for display
                    // if (Math.random() < 0.2) {
                    //   valueToAdd = 0;
                    // }

                    attempts++;
                    if (attempts > maxAttempts) {
                        // Fallback: If unable to find a non-winning value after many attempts,
                        // just pick any non-zero value that doesn't immediately create a 3-of-a-kind.
                        console.warn("Max attempts reached for generating losing value. May result in unintended win.");
                        break;
                    }
                } while (wouldCreateTriple(tempGeneratedValues, valueToAdd));

                tempGeneratedValues.push(valueToAdd);
            }

            // After initial generation, perform a final check and adjustment to guarantee no triples.
            let finalGeneratedValues = [...tempGeneratedValues];
            let needsAdjustment;
            do {
                needsAdjustment = false;
                const counts = {};
                finalGeneratedValues.forEach(v => { counts[v] = (counts[v] || 0) + 1; });

                for (const valStr in counts) {
                    const val = parseFloat(valStr);
                    if (counts[val] >= 3 && val > 0) { // Found a winning triple in a supposed losing card
                        needsAdjustment = true;
                        // Find one instance of this value and change it
                        for (let i = 0; i < finalGeneratedValues.length; i++) {
                            if (finalGeneratedValues[i] === val) {
                                let newValue;
                                // Try to get a non-matching value that's not 0, if possible
                                const availableNonWinningOptions = ALL_POSSIBLE_PRIZE_VALUES.filter((p) => p !== val && p !== 0);
                                if (availableNonWinningOptions.length > 0) {
                                  newValue = availableNonWinningOptions[Math.floor(Math.random() * availableNonWinningOptions.length)];
                                } else {
                                  // Fallback: If no other options are available (e.g., only one positive prize value exists)
                                  // This should not happen with current ALL_POSSIBLE_PRIZE_VALUES.
                                  // Default to a non-matching positive value if possible.
                                  newValue = PRIZE_OPTIONS[Math.floor(Math.random() * PRIZE_OPTIONS.length)];
                                }
                                finalGeneratedValues[i] = newValue;
                                break; // Only change one instance
                            }
                        }
                        // Re-check after adjustment
                        break;
                    }
                }
            } while (needsAdjustment);

            generatedValues.push(...finalGeneratedValues);
          }
          generatedValues.sort(() => Math.random() - 0.5); // Shuffle the values
          hiddenValues.current = generatedValues;
          onBalanceUpdate(balance - GAME_COST);
          setGameStarted(true);
          initializeCanvas(hiddenValues.current, true);
        } else {
          if (response.status === 402) {
            toast.error(t("insufficientBalance"));
            navigate("/wallet");
          } else {
            toast.error(t("failedToStartGame"));
          }
        }
      } catch (error) {
        toast.error(t("networkError"));
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("token");
          localStorage.removeItem("userId");
          navigate("/login");
        } else if (error.response?.status === 402) {
          toast.error(t("insufficientBalance"));
          navigate("/wallet");
        }
      } finally {
        setLoading(false);
        setIsTransitioning(false);
      }
    }, 300);
  }, [
    balance,
    GAME_COST,
    NUM_CIRCLES,
    ALL_POSSIBLE_PRIZE_VALUES,
    axiosInstance,
    initializeCanvas,
    navigate,
    onBalanceUpdate,
    t,
    PRIZE_OPTIONS
  ]);

  const handleFinishGame = useCallback(async () => {
    if (loading || !isScratched) return;

    setIsScratched(false);

    const allRevealed = revealedValuesRef.current.every((val) => val !== null);
    if (!cardId || !gameStarted || !allRevealed) return;

    setLoading(true);
    initializeCanvas(hiddenValues.current, false); // Draw the revealed state without cover

    setTimeout(async () => {
      setIsTransitioning(true);

      try {
        const payload = {
          id: cardId,
          prizeAmount: parseFloat(serverGamePrize), // Send the server-determined prize
        };

        const response = await axiosInstance.post("/scratch/finish", payload);
        if (response.data.success) {
          onBalanceUpdate(balance + parseFloat(serverGamePrize));
          setPrizeAmount(parseFloat(serverGamePrize)); // Set display prize from server-determined prize
        } else {
          toast.error(t("gameError"));
        }
      } catch (error) {
        toast.error(t("networkError"));
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("token");
          localStorage.removeItem("userId");
          navigate("/login");
        }
      } finally {
        setLoading(false);
        setTimeout(() => {
          setIsEndGameModalOpen(true);
          setIsEndGameModalFadingOut(false);
          setIsTransitioning(false);
        }, 1000);
      }
    }, 300);
  }, [
    cardId,
    gameStarted,
    serverGamePrize, // Dependency on serverGamePrize is crucial here
    axiosInstance,
    onBalanceUpdate,
    balance,
    loading,
    isScratched,
    initializeCanvas,
    hiddenValues,
    t,
    navigate,
  ]);

  const handlePlayAgain = useCallback(() => {
    setIsTransitioning(true);
    setIsEndGameModalFadingOut(true);

    setTimeout(() => {
      setIsEndGameModalOpen(false);
      setGameStarted(false);
      setRevealedValues(Array(NUM_CIRCLES).fill(null));
      hiddenValues.current = Array(NUM_CIRCLES).fill(null); // Reset hidden values
      initializeCanvas(Array(NUM_CIRCLES).fill(0), true); // Re-initialize canvas with dummy values before starting new game

      startGameConfirmed(); // Start a new game
    }, 300);
  }, [startGameConfirmed, initializeCanvas, NUM_CIRCLES]);

  const getClientPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;

    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (isScratched || !gameStarted) return;
      isDrawing.current = true;
      lastPoint.current = getClientPoint(e);
    },
    [isScratched, gameStarted, getClientPoint]
  );

  const handleMouseMove = useCallback(
    (e) => {
      const currentRevealedValues = revealedValuesRef.current;

      if (!isDrawing.current || isScratched || !gameStarted) return;
      e.preventDefault();

      const currentPoint = getClientPoint(e);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      ctx.beginPath();
      ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
      ctx.lineTo(currentPoint.x, currentPoint.y);
      ctx.strokeStyle = "rgba(0,0,0,1)";
      ctx.lineWidth = 40;
      ctx.lineCap = "round";
      ctx.globalCompositeOperation = "destination-out";
      ctx.stroke();

      lastPoint.current = currentPoint;

      const cellIndex = getCellIndex(currentPoint.x, currentPoint.y);

      if (cellIndex !== -1 && currentRevealedValues[cellIndex] === null) {
        const currentScratchPercentage =
          calculateAreaScratchPercentage(cellIndex);

        if (currentScratchPercentage >= scratchThreshold) {
          setRevealedValues((prev) => {
            const newValues = [...prev];
            newValues[cellIndex] = hiddenValues.current[cellIndex];
            return newValues;
          });
        }
      }
      const allAreasRevealed = revealedValuesRef.current.every(
        (val) => val !== null
      );
      if (allAreasRevealed && !isScratched) {
        setIsScratched(true);
        isDrawing.current = false;
      }
    },
    [
      isScratched,
      gameStarted,
      getClientPoint,
      getCellIndex,
      calculateAreaScratchPercentage,
      hiddenValues,
      scratchThreshold,
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    lastPoint.current = null;

    const allAreasRevealed = revealedValuesRef.current.every(
      (val) => val !== null
    );
    if (allAreasRevealed && !isScratched) {
      setIsScratched(true);
    }
  }, [isScratched]);

  const handleTouchMove = useCallback(
    (e) => {
      e.preventDefault();
      handleMouseMove(e);
    },
    [handleMouseMove]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (gameStarted) {
      canvas.addEventListener("mousedown", handleMouseDown);
      canvas.addEventListener("mousemove", handleMouseMove);
      canvas.addEventListener("mouseup", handleMouseUp);
      canvas.addEventListener("mouseleave", handleMouseUp);

      canvas.addEventListener("touchstart", handleMouseDown);
      canvas.addEventListener("touchmove", handleTouchMove);
      canvas.addEventListener("touchend", handleMouseUp);
      canvas.addEventListener("touchcancel", handleMouseUp);
    }

    // Clean up function
    return () => {
      if (canvas) {
        canvas.removeEventListener("mousedown", handleMouseDown);
        canvas.removeEventListener("mousemove", handleMouseMove);
        canvas.removeEventListener("mouseup", handleMouseUp);
        canvas.removeEventListener("mouseleave", handleMouseUp);

        canvas.removeEventListener("touchstart", handleMouseDown);
        canvas.removeEventListener("touchmove", handleTouchMove);
        canvas.removeEventListener("touchend", handleMouseUp);
        canvas.removeEventListener("touchcancel", handleMouseUp);
      }
    };
  }, [
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    gameStarted,
    handleTouchMove,
  ]);

  useEffect(() => {
    // This effect ensures the canvas is initialized with the correct hidden values
    // if `gameStarted` is true and `hiddenValues.current` are already set
    // (e.g., if component re-renders but game state persists).
    if (
      gameStarted &&
      hiddenValues.current.some((val) => val !== null) && // Check if hiddenValues actually contain data
      moneyBagImage.current &&
      moneyBagImage.current.complete
    ) {
      initializeCanvas(hiddenValues.current, true);
    }
  }, [gameStarted, initializeCanvas, moneyBagImage.current?.complete, hiddenValues.current]); // Add hiddenValues.current as dependency

  useEffect(() => {
    // Call handleFinishGame when scratching is complete and game is started
    if (isScratched && gameStarted) {
      handleFinishGame();
    }
  }, [isScratched, gameStarted, handleFinishGame]);

  useEffect(() => {
    // Initial canvas setup when the component mounts
    // Using a dummy array to set up the scratchable areas initially
    initializeCanvas(Array(NUM_CIRCLES).fill(0), true);
  }, [initializeCanvas, NUM_CIRCLES]);

  return (
    <PageWrapper>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <div className="relative w-full min-h-[calc(100vh-4rem)] text-white py-8">
        <div className="flex flex-col items-center justify-center h-full">
          <h1 className="text-4xl font-bold mb-6 text-yellow-400 drop-shadow-lg">
            {t("title")}
          </h1>
          <p
            className={`text-lg text-gray-300 mb-4 ${
              gameStarted && !isReadyModalOpen ? "" : "hidden"
            }`}
          >
            {t("scratchTheIcons")}
          </p>
          {loading && (
            <p className="text-lg text-gray-300 animate-pulse">
              {t("processing")}
            </p>
          )}

          <div className="relative w-[320px] h-[480px] z-10">
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full touch-none"
              aria-label={t("scratchArea")}
            />

            {revealedValues.map((value, index) => {
              if (value === null) return null;
              const circle = scratchCircles.current[index];
              if (!circle) return null;

              // Determine the value to display. If the actual value is 0,
              // pick a random positive value from PRIZE_OPTIONS for display purposes.
              let displayValue = value;
              if (value === 0) {
                const randomIndex = Math.floor(Math.random() * PRIZE_OPTIONS.length);
                displayValue = PRIZE_OPTIONS[randomIndex];
              }

              return (
                <div
                  key={index}
                  className="absolute rounded-full animate-pop-in glass-ball flex items-center justify-center text-center font-bold text-white overflow-hidden"
                  style={{
                    left: `${circle.x - circle.r}px`,
                    top: `${circle.y - circle.r}px`,
                    width: `${circle.r * 2}px`,
                    height: `${circle.r * 2}px`,
                    fontSize: `${circle.r * 0.4}px`,
                    zIndex: 15,
                    position: "absolute",
                  }}
                >
                  <div
                    className="absolute inset-0 sparkle rounded-full pointer-events-none"
                    style={{ backgroundImage: `url(./icons/sparkle.gif)` }}
                  />
                  <span style={{ zIndex: 1}}>{displayValue.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        </div>
        {((isReadyModalOpen && !isReadyModalFadingOut) ||
          (isEndGameModalOpen && !isEndGameModalFadingOut)) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40"></div>
        )}
        {isReadyModalOpen && (
          <div
            className={`fixed inset-0 flex items-center justify-center z-50 p-4 transition-all duration-300 ${
              isReadyModalFadingOut
                ? "opacity-0 scale-95 pointer-events-none"
                : "opacity-100 scale-100 pointer-events-auto"
            }`}
          >
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full text-center text-gray-800">
              <h2 className="text-3xl font-bold mb-4 text-gray-900">
                {t("readyToPlay")}
              </h2>
              <p className="text-md mb-4">
                {t("gameDescription", { cost: GAME_COST })}
              </p>

              <div className="mb-4">
                <button
                  onClick={() => setShowPrizesList((prev) => !prev)}
                  className="text-xl font-semibold text-gray-700 mb-2 flex items-center justify-center w-full py-2 px-4 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  {showPrizesList ? t("hidePrizes") : t("showPrizes")}
                  <span className="ml-2 text-yellow-500">🏆</span>
                </button>

                {showPrizesList && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-lg font-medium text-gray-700 mt-4">
                    {PRIZE_OPTIONS.map((prize, index) => (
                      <div
                        key={index}
                        className="flex justify-start items-center"
                      >
                        <span className="text-sm text-gray-500 mr-1">
                          {index + 1}.
                        </span>
                        <span className="font-bold">
                          {prize.toLocaleString()}
                        </span>
                        <span className="ml-1">{t("etb")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-4">
                <button
                  onClick={startGameConfirmed}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg shadow-lg transition-colors transform hover:scale-105"
                >
                  {t("play")}
                </button>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg shadow-lg transition-colors transform hover:scale-105"
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          </div>
        )}
        {isEndGameModalOpen && (
          <div
            className={`fixed inset-0 flex items-center justify-center z-50 p-4 transition-all duration-300 ${
              isEndGameModalFadingOut
                ? "opacity-0 scale-95 pointer-events-none"
                : "opacity-100 scale-100 pointer-events-auto"
            }`}
          >
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full text-center text-gray-800">
              <h2 className="text-3xl font-bold mb-4 text-gray-900">
                {prizeAmount > 0
                  ? t("congratulations")
                  : t("betterLuckNextTime")}
              </h2>
              <p className="text-md mb-4">
                {prizeAmount > 0
                  ? t("youWon", { amount: prizeAmount.toFixed(2) })
                  : t("tryAgain")}
              </p>
              <div className="flex flex-col gap-4">
                <button
                  onClick={handlePlayAgain}
                  disabled={loading}
                  className={`bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg shadow-lg transition-colors transform hover:scale-105 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {loading ? t("processing") : t("playAgain")}
                </button>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-purple-900 hover:bg-purple-800 text-white font-bold py-2 px-4 rounded-lg shadow-lg transition-colors transform hover:scale-105"
                >
                  {t("goBack")}
                </button>
              </div>
            </div>
          </div>
        )}
        {isScratched &&
          prizeAmount > 0 &&
          !isTransitioning && (
            <div className="fixed inset-0 z-45 pointer-events-none">
              <div className="absolute inset-0 confetti"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white animate-pop-in">
                <h2 className="text-5xl font-bold text-yellow-400 mb-4 drop-shadow-lg animate-bounce">
                  {t("congratulations")}
                </h2>
                <p className="text-3xl bg-green-800 px-8 py-4 rounded-lg shadow-xl">
                  {t("youWon", { amount: prizeAmount.toFixed(2) })}
                </p>
              </div>
            </div>
          )}
      </div>
    </PageWrapper>
  );
};

export default MoneyBagGamePage;
